import * as React from "react"
import { cn } from "@/lib/utils"

const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(
  ({ className, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)

    // Combine the forwarded ref with our internal ref
    React.useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement)

    // Function to adjust height
    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current
      if (textarea) {
        // Reset height temporarily to get the correct scrollHeight
        textarea.style.height = "10px"
        // Set the height to match the content
        textarea.style.height = `${textarea.scrollHeight}px`
      }
    }, [])

    // Adjust height on input
    React.useEffect(() => {
      const textarea = textareaRef.current

      if (textarea) {
        // Initial height adjustment
        adjustHeight()

        // Add event listener for input
        textarea.addEventListener("input", adjustHeight)

        // Cleanup
        return () => {
          textarea.removeEventListener("input", adjustHeight)
        }
      }
    }, [adjustHeight])

    return (
      <textarea
        className={cn(
          "flex min-h-10 w-full resize-none overflow-hidden rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        ref={textareaRef}
        {...props}
      />
    )
  },
)

Textarea.displayName = "Textarea"

export { Textarea }
